/**
 * ScrapeKing Design System v2 - App Layout
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC, PropsWithChildren, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTING } from '@constants/routing';
import styled, { keyframes } from 'styled-components';

import { Logo } from '@components/icons';

import OnboardingTour from '@/components/OnboardingTour/OnboardingTour';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sk-bg-primary);
  position: relative;
`;

// Header / Top Bar
const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  height: var(--sk-header-height);
  background: var(--sk-bg-secondary);
  border-bottom: 1px solid var(--sk-surface-border);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--sk-space-6);
  animation: ${fadeIn} 0.3s ease;
  gap: var(--sk-space-3);

  @media (max-width: 768px) {
    padding: 0 var(--sk-space-3);
    height: auto;
    min-height: var(--sk-header-height);
    gap: var(--sk-space-2);
  }

  /* Subtle gradient border at bottom */
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--sk-cyan-700) 20%,
      var(--sk-cyan-500) 50%,
      var(--sk-cyan-700) 80%,
      transparent 100%
    );
    opacity: 0.5;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
  text-decoration: none;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
    text-decoration: none;
  }

  @media (max-width: 768px) {
    svg {
      width: 100px;
      height: auto;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
  flex-shrink: 0;

  @media (max-width: 768px) {
    gap: var(--sk-space-2);
  }
`;

// Main content area with sidebar
const MainContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: visible;
  position: relative;
`;

// Sidebar Navigation
const Sidebar = styled.nav<{ $collapsed: boolean }>`
  width: ${props => (props.$collapsed ? 'var(--sk-sidebar-collapsed)' : 'var(--sk-sidebar-width)')};
  background: var(--sk-bg-secondary);
  border-right: 1px solid var(--sk-surface-border);
  display: flex;
  flex-direction: column;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: visible;
  position: relative;

  /* Decorative corner accent */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: linear-gradient(180deg, var(--sk-cyan-500) 0%, var(--sk-cyan-700) 30%, transparent 100%);
    opacity: 0.6;
  }

  @media (max-width: 1024px) {
    position: fixed;
    left: ${props => (props.$collapsed ? '-280px' : '0')};
    top: var(--sk-header-height);
    height: calc(100vh - var(--sk-header-height));
    z-index: 200;
    box-shadow: ${props => (props.$collapsed ? 'none' : '4px 0 20px rgba(0, 0, 0, 0.5)')};
  }
`;

const SidebarSection = styled.div`
  padding: var(--sk-space-4);
`;

const SidebarLabel = styled.div`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-2xs);
  font-weight: 600;
  color: var(--sk-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.15em;
  padding: var(--sk-space-2) var(--sk-space-3);
  margin-bottom: var(--sk-space-2);
`;

const NavList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--sk-space-1);
`;

const NavItem = styled.li`
  animation: ${slideInLeft} 0.3s ease backwards;

  &:nth-child(1) {
    animation-delay: 0ms;
  }
  &:nth-child(2) {
    animation-delay: 30ms;
  }
  &:nth-child(3) {
    animation-delay: 60ms;
  }
  &:nth-child(4) {
    animation-delay: 90ms;
  }
  &:nth-child(5) {
    animation-delay: 120ms;
  }
  &:nth-child(6) {
    animation-delay: 150ms;
  }
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
  padding: var(--sk-space-3) var(--sk-space-3);
  border-radius: var(--sk-radius-default);
  color: ${props => (props.$active ? 'var(--sk-cyan-400)' : 'var(--sk-text-secondary)')};
  background: ${props => (props.$active ? 'rgba(0, 200, 224, 0.1)' : 'transparent')};
  border: 1px solid ${props => (props.$active ? 'var(--sk-cyan-700)' : 'transparent')};
  text-decoration: none;
  font-family: var(--sk-font-body);
  font-size: var(--sk-text-sm);
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  /* Active indicator */
  ${props =>
    props.$active &&
    `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 60%;
      background: var(--sk-cyan-500);
      border-radius: 0 2px 2px 0;
      box-shadow: 0 0 10px var(--sk-cyan-glow);
    }
  `}

  &:hover {
    color: var(--sk-text-primary);
    background: var(--sk-surface-hover);
    text-decoration: none;
    transform: translateX(2px);
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    opacity: ${props => (props.$active ? 1 : 0.7)};
  }
`;

const NavText = styled.span<{ $collapsed: boolean }>`
  white-space: nowrap;
  opacity: ${props => (props.$collapsed ? 0 : 1)};
  transform: ${props => (props.$collapsed ? 'translateX(-10px)' : 'translateX(0)')};
  transition: all 0.2s ease;
`;

// Toggle button for sidebar - positioned to overlay the dividing line
const SidebarToggle = styled.button`
  position: absolute;
  right: 0;
  transform: translateX(50%);
  top: 35px;
  width: 28px;
  height: 28px;
  background: var(--sk-bg-secondary, #0f172a);
  border: 1px solid var(--sk-surface-border, #334155);
  border-radius: var(--sk-radius-full);
  color: var(--sk-text-secondary, #94a3b8);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 100;

  &:hover {
    background: var(--sk-surface-hover, #1e293b);
    color: var(--sk-text-primary, #f1f5f9);
    border-color: var(--sk-text-tertiary, #64748b);
  }

  svg {
    width: 14px;
    height: 14px;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

// Content Area
const Content = styled.main`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--sk-space-6);
  animation: ${fadeIn} 0.4s ease;

  @media (max-width: 768px) {
    padding: var(--sk-space-4);
  }
`;

const ContentInner = styled.div`
  max-width: var(--sk-content-max-width);
  margin: 0 auto;
`;

// Mobile menu button
const MobileMenuButton = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  background: var(--sk-surface-default);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-default);
  color: var(--sk-text-secondary);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--sk-surface-hover);
    color: var(--sk-cyan-500);
  }

  @media (max-width: 1024px) {
    display: flex;
  }
`;

// Mobile overlay
const MobileOverlay = styled.div<{ $visible: boolean }>`
  display: none;

  @media (max-width: 1024px) {
    display: block;
    position: fixed;
    inset: 0;
    top: var(--sk-header-height);
    background: rgba(0, 0, 0, 0.6);
    z-index: 150;
    opacity: ${props => (props.$visible ? 1 : 0)};
    pointer-events: ${props => (props.$visible ? 'auto' : 'none')};
    transition: opacity 0.3s ease;
  }
`;

// ═══════════════════════════════════════════════════════════════
// COMPONENT PROPS
// ═══════════════════════════════════════════════════════════════

interface AppLayoutV2Props extends PropsWithChildren {
  showSidebar?: boolean;
  headerActions?: React.ReactNode;
}

// ═══════════════════════════════════════════════════════════════
// ICONS (inline SVGs for demo)
// ═══════════════════════════════════════════════════════════════

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const ChevronLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="16"
    height="16"
  >
    <polyline points="15,18 9,12 15,6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    width="16"
    height="16"
  >
    <polyline points="9,18 15,12 9,6" />
  </svg>
);

// Navigation icons
const IconDatabase = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

const IconLayers = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12,2 2,7 12,12 22,7 12,2" />
    <polyline points="2,17 12,22 22,17" />
    <polyline points="2,12 12,17 22,12" />
  </svg>
);

const IconSend = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22,2 15,22 11,13 2,9 22,2" />
  </svg>
);

const IconActivity = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
  </svg>
);

const IconFlame = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
  </svg>
);

const IconSettings = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const IconInbox = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="22,12 16,12 14,15 10,15 8,12 2,12" />
    <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

const IconUsers = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconRocket = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const AppLayoutV2: FC<AppLayoutV2Props> = ({ children, showSidebar = true, headerActions }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Get current path and search params for active state
  const currentPath = location.pathname;
  const currentSearch = location.search;
  const currentFullPath = currentPath + currentSearch;

  // Helper to check if a nav item is active
  const isActiveRoute = (to: string) => {
    // If the route has query params, do an exact match with full path
    if (to.includes('?')) {
      return currentFullPath === to || currentFullPath.startsWith(`${to}&`);
    }
    // For settings, only match if there's no tab param OR if it's the profile tab (default)
    if (to === '/settings') {
      const hasTabParam = currentSearch.includes('tab=');
      const isProfileTab = currentSearch.includes('tab=PROFILE');
      return currentPath === '/settings' && (!hasTabParam || isProfileTab);
    }
    // For other routes, match pathname exactly
    return currentPath === to;
  };

  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const navItems = {
    home: [{ to: `/${ROUTING.DASHBOARD}`, label: 'Home', icon: <IconRocket /> }],
    getStarted: [{ to: '/setup', label: 'Email Setup', icon: <IconRocket /> }],
    campaigns: [
      { to: '/quick-campaign', label: 'Quick Campaign', icon: <IconSend /> },
      { to: '/import-campaign', label: 'Upload Leads', icon: <IconSend /> },
      { to: '/all-campaigns', label: 'All Campaigns', icon: <IconLayers /> },
      { to: '/database', label: 'Leads Database', icon: <IconDatabase /> },
    ],
    email: [
      { to: '/mailbox', label: 'Mailbox', icon: <IconInbox /> },
      { to: '/email-accounts', label: 'Accounts', icon: <IconUsers /> },
      { to: '/email-warmup', label: 'Warmup', icon: <IconFlame /> },
    ],
    settings: [
      { to: '/settings?tab=PROFILE', label: 'Profile', icon: <IconUsers /> },
      { to: '/settings?tab=BILLING', label: 'Billing', icon: <IconActivity /> },
      { to: '/settings?tab=TRANSACTIONS', label: 'Transactions', icon: <IconSettings /> },
    ],
  };

  return (
    <LayoutWrapper>
      {/* Header */}
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sk-space-4)' }}>
          <MobileMenuButton onClick={toggleMobileMenu}>
            <MenuIcon />
          </MobileMenuButton>
          <LogoContainer to={`/${ROUTING.DASHBOARD}`}>
            <Logo />
          </LogoContainer>
        </div>
        <HeaderActions>{headerActions}</HeaderActions>
      </Header>

      <MainContainer>
        {/* Sidebar */}
        {showSidebar && (
          <>
            <MobileOverlay $visible={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />
            <Sidebar
              $collapsed={
                sidebarCollapsed || (!mobileMenuOpen && typeof window !== 'undefined' && window.innerWidth <= 1024)
              }
            >
              <SidebarToggle onClick={toggleSidebar}>
                {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
              </SidebarToggle>

              {/* Home */}
              <SidebarSection>
                <NavList>
                  {navItems.home.map(item => (
                    <NavItem key={item.to}>
                      <NavLink to={item.to} $active={isActiveRoute(item.to)}>
                        {item.icon}
                        <NavText $collapsed={sidebarCollapsed}>{item.label}</NavText>
                      </NavLink>
                    </NavItem>
                  ))}
                </NavList>
              </SidebarSection>

              {/* Get Started Section */}
              <SidebarSection>
                {!sidebarCollapsed && <SidebarLabel>Get Started</SidebarLabel>}
                <NavList>
                  {navItems.getStarted.map(item => (
                    <NavItem key={item.to}>
                      <NavLink to={item.to} $active={isActiveRoute(item.to)}>
                        {item.icon}
                        <NavText $collapsed={sidebarCollapsed}>{item.label}</NavText>
                      </NavLink>
                    </NavItem>
                  ))}
                </NavList>
              </SidebarSection>

              {/* Campaigns Section */}
              <SidebarSection>
                {!sidebarCollapsed && <SidebarLabel>Campaigns</SidebarLabel>}
                <NavList>
                  {navItems.campaigns.map(item => (
                    <NavItem key={item.to}>
                      <NavLink to={item.to} $active={isActiveRoute(item.to)}>
                        {item.icon}
                        <NavText $collapsed={sidebarCollapsed}>{item.label}</NavText>
                      </NavLink>
                    </NavItem>
                  ))}
                </NavList>
              </SidebarSection>

              {/* Email Section */}
              <SidebarSection>
                {!sidebarCollapsed && <SidebarLabel>Email</SidebarLabel>}
                <NavList>
                  {navItems.email.map(item => (
                    <NavItem key={item.to}>
                      <NavLink to={item.to} $active={isActiveRoute(item.to)}>
                        {item.icon}
                        <NavText $collapsed={sidebarCollapsed}>{item.label}</NavText>
                      </NavLink>
                    </NavItem>
                  ))}
                </NavList>
              </SidebarSection>

              {/* Settings Section */}
              <SidebarSection>
                {!sidebarCollapsed && <SidebarLabel>Settings</SidebarLabel>}
                <NavList>
                  {navItems.settings.map(item => (
                    <NavItem key={item.to}>
                      <NavLink to={item.to} $active={isActiveRoute(item.to)}>
                        {item.icon}
                        <NavText $collapsed={sidebarCollapsed}>{item.label}</NavText>
                      </NavLink>
                    </NavItem>
                  ))}
                </NavList>
              </SidebarSection>
            </Sidebar>
          </>
        )}

        {/* Main Content */}
        <Content>
          <ContentInner>{children}</ContentInner>
        </Content>
      </MainContainer>
      <OnboardingTour />
    </LayoutWrapper>
  );
};

export default AppLayoutV2;
